import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Users, Camera, MessageCircle, Crown, Mic, Eye } from 'lucide-react';

const LiveDealerGuide = () => {
  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
              Live Dealer Gaming Guide
            </h1>
            <p className="text-xl text-book-brown-dark leading-relaxed">
              Immerse yourself in authentic casino atmosphere with strategies for live dealer games and social interaction.
            </p>
          </div>

          <div className="mb-16 bg-book-beige rounded-xl p-8 shadow-book-inset">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Our Guide Writing Philosophy</h2>
            <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
              <p>Every guide published on Brit Ember represents extensive research and deep industry insights...</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-6">Bridging Digital and Traditional Casino Experiences</h2>
            <p>Live dealer gaming represents the pinnacle of online casino innovation...</p>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-8 text-center">Live Dealer Experience Analytics</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Game Popularity</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Live Blackjack</span>
                    <span className="font-semibold text-book-brown">42%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Live Roulette</span>
                    <span className="font-semibold text-book-brown">35%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Live Baccarat</span>
                    <span className="font-semibold text-book-brown">15%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Live Poker</span>
                    <span className="font-semibold text-book-brown">8%</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Studio Quality Comparison</h3>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Premium Studios</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-green-500 h-3 rounded-full" style={{width: '95%'}}></div>
                      </div>
                      <span className="font-semibold text-green-600">95%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Standard Studios</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-yellow-500 h-3 rounded-full" style={{width: '78%'}}></div>
                      </div>
                      <span className="font-semibold text-yellow-600">78%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Basic Studios</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-orange-500 h-3 rounded-full" style={{width: '65%'}}></div>
                      </div>
                      <span className="font-semibold text-orange-600">65%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Interaction Features</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Chat Functionality</span>
                    <div className="w-16 bg-green-500 rounded-full h-3"></div>
                    <span className="font-semibold text-green-600">98%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Dealer Interaction</span>
                    <div className="w-14 bg-green-500 rounded-full h-3"></div>
                    <span className="font-semibold text-green-600">95%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Multi-Camera Views</span>
                    <div className="w-12 bg-yellow-500 rounded-full h-3"></div>
                    <span className="font-semibold text-yellow-600">85%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">VIP Tables</span>
                    <div className="w-8 bg-purple-500 rounded-full h-3"></div>
                    <span className="font-semibold text-purple-600">70%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
            <p>Live dealer gaming represents the pinnacle of online casino innovation...</p>
            <div className="bg-book-beige rounded-lg p-6 my-8">
              <h4 className="text-xl font-bold text-book-brown mb-4">Essential Live Dealer Gaming Principles</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Users className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Choose reputable studios with professional dealers and high-quality streams</span>
                </li>
                <li className="flex items-start">
                  <MessageCircle className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Engage respectfully with dealers and other players through chat features</span>
                </li>
                <li className="flex items-start">
                  <Camera className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Take advantage of multiple camera angles for enhanced viewing experience</span>
                </li>
                <li className="flex items-start">
                  <Crown className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Consider VIP tables for exclusive experiences and higher limits</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LiveDealerGuide;
